import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import DashboardWrapper from "../../components/DashboardWrapper";
import CrudTable from "../../components/dashboard/table/CrudTable";
import CrudForm from "../../components/dashboard/form/CrudForm";
import { useCrud } from "../../hooks/useCrud";
import Swal from "sweetalert2";

const generateSlug = (text) =>
  text.toString().toLowerCase().trim().replace(/\s+/g, "-");

export default function Category() {
  const navigate = useNavigate();

  const {
    data: categories,
    loading,
    errors,
    createItem,
    updateItem,
    deleteItem,
  } = useCrud(`${process.env.REACT_APP_API_URL}/backend/categories`, [
    "category_image",
  ]);

  const [editingItem, setEditingItem] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  // ✅ API BASED FIELDS
  const allowedFields = useMemo(
    () => [
      "name",
      "category_image",
      "status",
      "seo_title",
      "seo_description",
      "seo_keywords",
      "description",
    ],
    []
  );

  const getFormData = useCallback(
    (item) => {
      if (!item)
        return {
          name: "",
          category_image: null,
          status: 1,
          seo_title: "",
          seo_description: "",
          seo_keywords: "",
          description: "",
        };

      return allowedFields.reduce((acc, field) => {
        acc[field] =
          field === "status"
            ? Number(item[field] ?? 1)
            : field === "category_image"
              ? item[field] || null
              : item[field] || "";
        return acc;
      }, {});
    },
    [allowedFields]
  );

  const hasDuplicate = useCallback(
    (name, slug, id = null) =>
      categories.some(
        (cat) =>
          (cat.name.toLowerCase() === name.toLowerCase() ||
            cat.slug.toLowerCase() === slug.toLowerCase()) &&
          cat.id !== id
      ),
    [categories]
  );

  const showSwal = useCallback((title, text, icon) => {
    Swal.fire({
      title,
      text,
      icon,
      confirmButtonText: "OK",
      customClass: { confirmButton: "btn btn-primary" },
      buttonsStyling: false,
    });
  }, []);

  const handleSubmit = useCallback(
    async (formData) => {
      if (!formData.name.trim()) {
        showSwal("Error!", "Category name is required.", "warning");
        return;
      }

      formData.slug = generateSlug(formData.name);

      if (hasDuplicate(formData.name, formData.slug, editingItem?.id)) {
        showSwal(
          "Duplicate Found!",
          "A category with the same name already exists.",
          "warning"
        );
        return;
      }

      const dataToSend = {};

      allowedFields.forEach((field) => {
        if (field === "category_image") {
          if (formData.category_image instanceof File) {
            dataToSend.category_image = formData.category_image;
          }
        } else {
          dataToSend[field] = formData[field];
        }
      });
      dataToSend.slug = formData.slug;

      try {
        if (editingItem) {
          await updateItem(editingItem.id, dataToSend);
          showSwal("Updated!", "Category updated successfully.", "success");
        } else {
          await createItem(dataToSend);
          showSwal("Created!", "Category created successfully.", "success");
        }

        setModalOpen(false);
        setEditingItem(null);
      } catch (error) {
        showSwal("Error!", error.message || "Something went wrong.", "error");
      }
    },
    [allowedFields, editingItem, updateItem, createItem, hasDuplicate, showSwal]
  );

  const openModal = useCallback((item = null) => {
    setEditingItem(item);
    setModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setModalOpen(false);
    setEditingItem(null);
  }, []);

  const handleDelete = useCallback(
    (id) => {
      Swal.fire({
        title: "Are you sure?",
        text: "This action cannot be undone!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, delete it!",
        cancelButtonText: "Cancel",
        customClass: {
          confirmButton: "btn btn-primary m-1",
          cancelButton: "btn btn-danger m-1",
        },
        buttonsStyling: false,
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            await deleteItem(id);
            showSwal("Deleted!", "Category deleted.", "success");
          } catch (error) {
            Swal.fire("Error!", error.message || "Delete failed.", "error");
          }
        }
      });
    },
    [deleteItem]
  );

  const handleToggleStatus = useCallback(
    async (item) => {
      try {
        const updatedData = {
          name: item.name,
          slug: item.slug,
          status: item.status ? 0 : 1,
        };

        await updateItem(item.id, updatedData);
        showSwal("Success!", "Status updated.", "success");
      } catch {
        showSwal("Error!", "Failed to update status.", "error");
      }
    },
    [updateItem, showSwal]
  );

  const tableData = useMemo(
    () => categories.map((item, idx) => ({ ...item, sl: idx + 1 })),
    [categories]
  );

  return (
    <DashboardWrapper>
      <div className="container-fluid mt-4">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <button
            className="btn btn-secondary"
            onClick={() => navigate(-1)}
          >
            Back
          </button>

          <button
            type="button"
            className="btn btn-primary"
            onClick={() => openModal()}
            style={{marginRight:"1.5rem"}}
          >
            Create Category
          </button> 
        </div>

        <div className="card shadow">
          <div className="card-header">
            <h4 className="mb-0">Categories</h4>
          </div>

          {modalOpen && (
            <div
              className="modal fade show"
              style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
            >
              <div className="modal-dialog modal-dialog-centered ">
                <div className="modal-content">

                  <div className="modal-header">
                    <h5 className="modal-title px-5">
                      {editingItem ? "Edit Category" : "Create Category"}
                    </h5>

                    <button
                      className="btn-close"
                      onClick={closeModal}
                    />
                  </div>

                  <div className="modal-body px-5 py-4">
                    <CrudForm
                      initialData={getFormData(editingItem)}
                      onSubmit={handleSubmit}
                      allowedFields={allowedFields}
                      fileFields={[
                        { name: "category_image", multiple: false }
                      ]}
                    />
                  </div>

                </div>
              </div>
            </div>
          )}

          {loading ? (
            <div className="text-center p-5">
              <div className="spinner-border text-primary" />
            </div>
          ) : (
            <div className="card-body">
              <CrudTable
                data={tableData}
                onEdit={openModal}
                onDelete={handleDelete}
                onToggleStatus={handleToggleStatus}
                columns={["sl", "id", "category_image", "name", "status"]}
              />
            </div>
          )}
        </div>
      </div>
    </DashboardWrapper>
  );
}